import React, { FC } from 'react'
import { clone } from 'lodash'
import { Box, Flex } from 'front-end/src/components'

import { PathwayEnrolmentStatusEnum } from 'front-end/src/graphql/public/PublicSchemaTypes'
import { brandColors } from 'shared/basicPalette'

import { Card } from '../../../components/common/card/Card'
import CardTitle from '../../../components/common/card/CardTitle'
import CircleProgress from '../../../components/common/circle-progress/CircleProgress'
import IconDoc from '../../../components/common/icon/IconDoc'
import { imageVariants2srcSetDensity } from '../../../components/utils/imageVariants2srcSetDensity'
import Tags from '../../../components/common/tag/Tags'
import { CardAssignedPin } from '../../../components/common/card/CardAssignedPin'
import { PathwayResourcesPlural } from '../../manage-pathways-page/components/PathwayItem'
import { PathwayCardFragment } from './PathwayCard.codegen'
import './PathwayCard.less'
import { CardStatuses } from '../../../components/common/card/CardStatuses'
import { StatusProgressDue } from '../../../components/common/status/Status'
import { CardStatusesType } from '../../workspace-page/resource-card/ResourceCard'
import { css } from '@emotion/react'

interface IProps {
  pathway: PathwayCardFragment
  progress?: number | null
  linkUrl: string
}

const PathwayCard: FC<IProps> = (props) => {
  const { pathway, progress, linkUrl } = props
  const { currentEnrolment } = pathway
  const targetDate = currentEnrolment && currentEnrolment.target_date
  const isFailed =
    currentEnrolment?.status === PathwayEnrolmentStatusEnum.FAILED
  const isCompleted =
    currentEnrolment?.status === PathwayEnrolmentStatusEnum.COMPLETED

  const shouldRenderPin = !!(targetDate && !isCompleted)

  const isAssignmentOverdue =
    currentEnrolment?.status === PathwayEnrolmentStatusEnum.OVERDUE

  const coverImageSrcSet = pathwayCardCoverImageSrcSet(
    pathway.coverImageVariants
  )

  const subtitle = (
    <Flex align="center">
      <IconDoc boxSize="14px" mr="6px" />
      <PathwayResourcesPlural value={pathway.currentTopicVersionsCount} />

      <Box
        className="PathwayCard__tags"
        css={css`
          .Tag {
            color: ${!!pathway.coverImage
              ? brandColors.grey[800]
              : brandColors.grey[600]};
          }
        `}
      >
        <Tags size="small" allowFocus={false} items={pathway.tags} />
      </Box>
    </Flex>
  )

  const statuses: CardStatusesType = []

  if (targetDate && !isCompleted && currentEnrolment?.status) {
    statuses.push(
      <StatusProgressDue
        dueDate={targetDate}
        status={currentEnrolment.status}
      />
    )
  }

  return (
    <Card
      className="PathwayCard"
      image={pathway.coverImage && pathway.coverImage.cdnUrl}
      imageSrcSet={coverImageSrcSet}
      linkUrl={linkUrl}
    >
      <CardStatuses statuses={statuses} offsetRight={shouldRenderPin} />
      <CardTitle
        title={pathway.title}
        subtitle={subtitle}
        subtitleDark={!pathway.coverImage}
      />
      {shouldRenderPin && currentEnrolment?.status && (
        <CardAssignedPin status={currentEnrolment.status} />
      )}
      <div className="PathwayCard__progress">
        {typeof progress === 'number' && (
          <CircleProgress
            progress={progress / 100}
            failed={isFailed}
            completed={isCompleted}
            lineWidth={8}
            lineColor={
              isAssignmentOverdue ? brandColors.red.mid : brandColors.blue.mid
            }
            backgroundColor="rgba(255, 255, 255, 0)"
            backgroundLineColor={'rgba(220, 220, 220, .5)'}
          >
            <Box borderColor={brandColors.blue.mid} className="arrow-right" />
          </CircleProgress>
        )}
      </div>
    </Card>
  )
}

export function pathwayCardCoverImageSrcSet(coverImageVariants) {
  if (!coverImageVariants) {
    return
  }
  return imageVariants2srcSetDensity(
    coverImageVariants
      .filter((imageVariant) => {
        return (
          imageVariant.size_key &&
          ['600x300', '1400x500'].includes(imageVariant.size_key)
        )
      })
      .map((imageVariant) => {
        const withDensity = clone(imageVariant)

        switch (withDensity.size_key) {
          case '600x300':
            withDensity.density = 1
            break
          case '1400x500':
            withDensity.density = 2
            break
        }
        return withDensity
      })
  )
}

export default PathwayCard
